import { DownloadOutlined, DownOutlined, FileOutlined } from '@ant-design/icons';
import { Button, Col, Dropdown, notification, Radio, Space, Table, Typography } from 'antd';
import moment from 'moment';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import useAPIQuery from '../../../api/useAPIQuery';
import Filter from '../../../components/Filter';
import SearchParam from '../../../components/SearchParam';
import ImportFileModal from '../../../components/Staff/ImportFIleModal';
import { withRouteChange } from '../../../services/APM';
import { ImportFileCollection, ImportFileCollectionParams } from '../../../types';

const ImportFiles = () => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const importFiles = useAPIQuery(
    [
      '/import-files/',
      {
        params: {
          type: (searchParams.get('type') as ImportFileCollectionParams['type']) ?? undefined,
          page: parseInt(searchParams.get('page') || '1', 10),
          page_size: parseInt(searchParams.get('pageSize') || '10', 10),
        },
      },
    ],
    {
      refetchInterval(data) {
        if (
          data?.results.some((importFile) => ['PENDING', 'IN_PROGRESS'].includes(importFile.status))
        ) {
          return 5_000;
        }

        return false;
      },
      keepPreviousData: true,
    }
  );

  const [importType, setImportType] = useState<ImportFileCollection['type'] | null>(null);

  return (
    <>
      <Helmet>
        <title>{t('Importar Arquivos | Área do CS | Recrutamento e Seleção')}</title>
      </Helmet>
      {importType && (
        <ImportFileModal
          key="filesModal"
          type={importType}
          open={Boolean(importType)}
          onSuccess={() => {
            setImportType(null);

            notification.success({
              message: t('Arquivo adicionado com sucesso!'),
              description: t('O arquivo será processados em alguns minutos.'),
              duration: 6,
            });
          }}
          onCancel={() => setImportType(null)}
        />
      )}
      <Filter>
        <Filter.Item
          alwaysOn
          label={t('Tipo')}
          name="type"
          selected={
            <Filter.Selection name="type" onSetSearchParams={{ page: '1' }}>
              <Filter.Tag defaultValue="IMPORT_CANDIDATES">
                <Trans>Banco de Talentos</Trans>
              </Filter.Tag>
              <Filter.Tag defaultValue="MATCH_RESULTS">
                <Trans>Match dos candidatos</Trans>
              </Filter.Tag>
              <Filter.Tag defaultValue="TEST_RESULTS">
                <Trans>Notas dos candidatos</Trans>
              </Filter.Tag>
            </Filter.Selection>
          }
        >
          <Space direction="vertical">
            <SearchParam name="type" onSetSearchParams={{ page: '1' }}>
              <Radio.Group>
                <Space direction="vertical">
                  <Radio value="IMPORT_CANDIDATES">
                    <Trans>Banco de Talentos</Trans>
                  </Radio>
                  <Radio value="MATCH_RESULTS">
                    <Trans>Match dos candidatos</Trans>
                  </Radio>
                  <Radio value="TEST_RESULTS">
                    <Trans>Notas dos candidatos</Trans>
                  </Radio>
                </Space>
              </Radio.Group>
            </SearchParam>
            <SearchParam
              array
              name="type"
              onSetSearchParams={{ page: '1' }}
              valuePropName="disabledValue"
            >
              <Filter.Button>
                <Trans>Limpar</Trans>
              </Filter.Button>
            </SearchParam>
          </Space>
        </Filter.Item>
        <Col flex={1} />
        <Col>
          <Dropdown
            menu={{
              onClick: ({ key }) => setImportType(key as ImportFileCollection['type']),
              items: [
                { key: 'IMPORT_CANDIDATES', label: t('Banco de Talentos') },
                { key: 'MATCH_RESULTS', label: t('Match dos candidatos') },
                { key: 'TEST_RESULTS', label: t('Notas dos candidatos') },
              ],
            }}
          >
            <Button type="primary">
              <Trans>Importar arquivo</Trans> <DownOutlined />
            </Button>
          </Dropdown>
        </Col>
      </Filter>
      <Table
        dataSource={importFiles.data?.results ?? []}
        loading={importFiles.isLoading}
        pagination={{
          showTotal: (total) => {
            return t(`Total: {{total}} arquivos importados`, { total });
          },
          total: importFiles.data?.count,
          onChange: (page, pageSize) => {
            searchParams.set('page', page.toString());
            searchParams.set('pageSize', pageSize.toString());
            setSearchParams(searchParams);
          },
          current: parseInt(searchParams.get('page') || '1', 10),
          pageSize: parseInt(searchParams.get('pageSize') || '10', 10),
        }}
        rowKey="id"
      >
        <Table.Column<ImportFileCollection> title="#" dataIndex="id" />
        <Table.Column<ImportFileCollection>
          title={t('Tipo')}
          dataIndex="type"
          render={(value: ImportFileCollection['type']) => (
            <>
              {value === 'IMPORT_CANDIDATES' && (
                <Typography.Text>
                  <Trans>Banco de Talentos</Trans>
                </Typography.Text>
              )}
              {value === 'MATCH_RESULTS' && (
                <Typography.Text>
                  <Trans>Match dos candidatos</Trans>
                </Typography.Text>
              )}
              {value === 'TEST_RESULTS' && (
                <Typography.Text>
                  <Trans>Notas dos candidatos</Trans>
                </Typography.Text>
              )}
            </>
          )}
        />
        <Table.Column<ImportFileCollection>
          title={t('Enviado por')}
          dataIndex="created_by"
          render={(value: ImportFileCollection['created_by']) => (
            <Typography.Text>{value.full_name || value.username}</Typography.Text>
          )}
        />
        <Table.Column<ImportFileCollection>
          title={t('Enviado em')}
          dataIndex="created_at"
          render={(value) => <Typography.Text>{moment(value).format('LLL')}</Typography.Text>}
        />
        <Table.Column<ImportFileCollection>
          title={t('Resultado')}
          render={(value, record) => (
            <>
              {record.status === 'PENDING' && (
                <Button type="link" icon={<FileOutlined />} disabled>
                  {' '}
                  <Trans>Aguardando</Trans>
                </Button>
              )}
              {record.status === 'IN_PROGRESS' && (
                <Button type="link" icon={<FileOutlined />} disabled>
                  {' '}
                  <Trans>Processando</Trans>
                </Button>
              )}
              {record.status === 'SUCCESS' && record.result && (
                <a href={record.result}>
                  <Button type="link" icon={<DownloadOutlined />}>
                    {' '}
                    <Trans>Concluído</Trans>
                  </Button>
                </a>
              )}
              {record.status === 'FAILED' &&
                (record.result ? (
                  <a href={record.result}>
                    <Button danger type="link" icon={<DownloadOutlined />}>
                      {' '}
                      <Trans>Arquivo inválido</Trans>
                    </Button>
                  </a>
                ) : (
                  <Typography.Text disabled type="danger">
                    <Trans>Erro, verifique com suporte.</Trans>
                  </Typography.Text>
                ))}
            </>
          )}
        />
      </Table>
    </>
  );
};

export default withRouteChange()(ImportFiles);
